import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
  useTheme,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const Footer: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Tax Tools Hub', path: '/tax-tools' },
    { name: 'Insights', path: '/resources' },
    { name: 'Contact', path: '/contact' },
    { name: 'Pay Online', path: '/payment' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms & Conditions', path: '/terms' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, #081E35 100%)`,
        color: '#C8D3E0',
        pt: 8,
        pb: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box mb={2}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src="/fingard-white.jpg"
                  alt="Fingard Logo"
                  sx={{ height: 70, mr: 4 }}
                />
                
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your trusted partner in finance & compliance. Professional solutions for your financial success.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  aria-label="Facebook"
                  component="a"
                  href="https://www.facebook.com/profile.php?id=61591174234850"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  aria-label="Twitter"
                  component="a"
                  href="https://x.com/fingardpartners"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  aria-label="LinkedIn"
                  component="a"
                  href="https://www.linkedin.com/in/fingard-partners-20135737b/"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  component="a"
                  href="https://www.instagram.com/fingard.official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ color: '#FFFFFF' }} gutterBottom>
              Quick Links
            </Typography>
            <Box component="nav">
              {quickLinks.map((link) => (
                <Link
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  color="inherit"
                  variant="body2"
                  display="block"
                  gutterBottom
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main',
                    },
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" sx={{ color: '#FFFFFF' }} gutterBottom>
              Contact Info
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <EmailIcon color="primary" sx={{ mr: 1, mt: '2px' }} />
              <Typography variant="body2">
                <Link href="mailto:support@fingard.co" color="inherit" underline="hover">
                  support@fingard.co
                </Link>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2">
                <Link href="tel:+91 9116033575" color="inherit" underline="hover">
                  +91 9116033575
                </Link>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <LocationOnIcon color="primary" sx={{ mr: 1, mt: '2px' }} />
              <Typography variant="body2">
                Plot No: 3-B, Sangram Colony Gate No. 2,<br />
                Near Mahavir School, Mahavir Marg,<br />
                C-Scheme, Jaipur-302001,<br />
                Rajasthan, India
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={{ color: '#FFFFFF' }} gutterBottom>
              Legal
            </Typography>
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                component={RouterLink}
                to={link.path}
                color="inherit"
                variant="body2"
                display="block"
                gutterBottom
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                  },
                  mb: 1,
                  display: 'block',
                }}
              >
                {link.name}
              </Link>
            ))}
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.15)' }} />
        
        <Box textAlign="center">
          <Typography variant="body2" sx={{ color: '#C8D3E0' }}>
            © {currentYear} Fingard. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
